import Icon from "@ant-design/icons";
import { Card, Checkbox, Divider, Flex, Form, theme, Typography } from "antd";
import Mada from "assets/icons/mada.svg";
import ApplePay from "assets/icons/apple-pay-logo.svg";
import Visa from "assets/icons/visa.svg";
import Mastercard from "assets/icons/mastercard-new.svg";
import { FunctionComponent, SVGAttributes, useMemo } from "react";
import { useStepValidation } from "hooks/get_paid";
import { Trans, useTranslation } from "react-i18next";
import { GET_PAID_STEP } from "types/get_paid";
import { CheckboxChangeEvent } from "antd/es/checkbox";

const usePaymentMethods = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const data = useMemo(
    () => [
      {
        id: "mada",
        title: t("activerecord.attributes.paragraphs.payment_methods.mada"),
        description: (
          <Typography.Text
            type="secondary"
            style={{ fontSize: token.fontSizeSM }}
          >
            {t(
              "templates.integrations.getpaid_activation.activation.commission"
            )}
            : <span style={{ color: token.colorLink }}>1.5%</span>
          </Typography.Text>
        ),
        iconList: [
          {
            Icon: Mada,
            size: 50,
          },
          {
            Icon: ApplePay,
            size: 43,
          },
        ],
      },
      {
        id: "cc",
        title: t(
          "activerecord.attributes.paragraphs.payment_methods.visa_mastercard"
        ),
        description: (
          <Typography.Text
            type="secondary"
            style={{ fontSize: token.fontSizeSM }}
          >
            {t(
              "templates.integrations.getpaid_activation.activation.commission"
            )}
            : <span style={{ color: token.colorLink }}>2.8%</span>
          </Typography.Text>
        ),
        iconList: [
          {
            Icon: Mastercard,
            size: 30,
          },
          {
            Icon: Visa,
            size: 52,
          },
          {
            Icon: ApplePay,
            size: 43,
          },
        ],
      },
    ],
    []
  );
  return data;
};

const ActivationStep = ({
  handleShouldGoNext,
}: {
  handleShouldGoNext: (shouldGoNext: boolean) => void;
}) => {
  const { token } = theme.useToken();
  const paymentMethods = usePaymentMethods();
  const { t, i18n } = useTranslation();

  useStepValidation({ handleShouldGoNext });

  return (
    <>
      <Typography.Title
        level={4}
        style={{
          marginBottom: 0,
          fontWeight: token.fontWeightStrong,
        }}
      >
        {t("templates.integrations.getpaid_activation.activation.header")}
      </Typography.Title>
      <Typography.Text
        type="secondary"
        style={{ marginBottom: token.marginLG }}
      >
        {t("templates.integrations.getpaid_activation.activation.subtitle")}
      </Typography.Text>
      <Divider />
      <Flex gap={16} vertical>
        <Typography.Title level={5}>
          {t(
            "templates.integrations.getpaid_activation.activation.payment_methods"
          )}
        </Typography.Title>
        {paymentMethods.map((paymentMethod) => (
          <PaymentMethodCard
            key={paymentMethod.id}
            paymentMethod={paymentMethod.title}
            description={paymentMethod.description}
            iconList={paymentMethod.iconList}
          />
        ))}
        <Form.Item
          name={[GET_PAID_STEP.ACTIVATION, "termsConditionsTahseel"]}
          rules={[
            {
              required: true,
              message: t(
                "templates.integrations.getpaid_activation.activation.terms_and_conditions_error"
              ),
            },
          ]}
          valuePropName="checked"
          getValueFromEvent={(e: CheckboxChangeEvent) => {
            const value = e.target.checked;
            if (value === false) return undefined;
            return value;
          }}
        >
          <Checkbox>
            <Typography.Text style={{ fontWeight: "normal" }}>
              <Trans
                i18nKey="templates.integrations.getpaid_activation.activation.terms_and_conditions"
                components={{
                  a: (
                    <a
                      href={
                        i18n.language === "ar"
                          ? "/ara/tahseel-tc"
                          : "/en/tahseel-tcen"
                      }
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </Typography.Text>
          </Checkbox>
        </Form.Item>
      </Flex>
    </>
  );
};

const PaymentMethodCard = ({
  paymentMethod,
  description,
  iconList,
}: {
  paymentMethod: string;
  description: string | React.ReactNode;
  iconList: {
    Icon: FunctionComponent<SVGAttributes<SVGElement>>;
    size: number;
  }[];
}) => {
  const { token } = theme.useToken();
  return (
    <Card style={{ width: "100%", backgroundColor: token.colorBgLayout }}>
      <Flex justify="space-between" gap={16}>
        <Card.Meta title={paymentMethod} description={description} />
        <Flex gap={8}>
          {iconList.map((iconObj, index) => (
            <Icon
              key={index}
              component={iconObj.Icon}
              style={{ fontSize: iconObj.size }}
            />
          ))}
        </Flex>
      </Flex>
    </Card>
  );
};

export default ActivationStep;
